import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SeoHead from '@/components/feature/SeoHead';
import JsonLd from '@/components/feature/JsonLd';
import PageHero from '@/components/feature/PageHero';
import Reveal from '@/components/feature/Reveal';
import { contactInfo, siteInfo, stats, testimonials } from '@/mocks/site';

const principles = [
  { title: '先说清楚规则', desc: '活动怎么玩、奖励怎么拿、什么时候到账，一律写在最前面，不用你翻半天。', icon: 'ri-file-list-3-line' },
  { title: '不堆砌关键词', desc: '内容围绕真实参与过程来写，该解释的地方解释清楚，不为了凑字数重复同一句话。', icon: 'ri-quill-pen-line' },
  { title: '信息保持更新', desc: '规则有调整就同步改，页面标注最近更新时间，避免你拿着旧规则去算预期。', icon: 'ri-refresh-line' },
  { title: '安全永远优先', desc: '不诱导付费、不索要验证码，遇到可疑情况主动提醒，安全提醒贯穿每个活动页。', icon: 'ri-shield-check-line' },
];

const positioning = [
  { title: '我们做什么', desc: '把分散在不同地方的福利活动、抽奖玩法、任务奖励规则汇总起来，写成普通人一眼能看懂的样子。', icon: 'ri-compass-3-line', tone: 'primary' },
  { title: '为什么做这件事', desc: '福利活动本身不复杂，复杂的是规则写得绕。我们希望减少「看不懂所以放弃」的情况。', icon: 'ri-heart-3-line', tone: 'accent' },
  { title: '我们不做什么', desc: '不做收费代领，不做夸大宣传，也不承诺任何无法保证的中奖结果，只做信息的整理与说明。', icon: 'ri-forbid-2-line', tone: 'secondary' },
];

const toneMap: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-800',
  secondary: 'bg-secondary-100 text-secondary-800',
};

const structuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      name: '关于我们 - 爆瓜福利',
      description: '了解爆瓜福利的定位、发展理念、内容原则与联系方式。',
      url: 'https://ikinxod.cn/about',
      inLanguage: 'zh-CN',
    },
    {
      '@type': 'Organization',
      name: siteInfo.name,
      url: 'https://ikinxod.cn/',
      email: siteInfo.email,
      description: siteInfo.intro,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ikinxod.cn/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://ikinxod.cn/about' },
      ],
    },
  ],
};

export default function About() {
  return (
    <>
      <SeoHead
        path="/about"
        title="关于我们 - 爆瓜福利内容定位与联系方式"
        description="爆瓜福利是一个专注吃瓜娱乐福利的内容整理站，本页介绍网站定位、发展理念、内容原则与联系方式，让你了解我们为什么做这件事、以及如何做得更靠谱。"
        keywords="关于爆瓜福利,爆瓜福利介绍,福利站定位,内容原则,联系方式"
      />
      <JsonLd data={structuredData} />

      <Layout>
        <PageHero
          icon="ri-team-line"
          eyebrow="关于我们"
          title="把福利规则讲清楚，是我们的全部工作"
          desc="爆瓜福利不是活动主办方，而是一个专注于「把活动讲明白」的内容整理站。我们相信，规则透明本身就是一种福利。"
          crumbs={[{ label: '首页', path: '/' }, { label: '关于我们' }]}
        />

        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            <div className="w-full h-64 md:h-[420px] rounded-2xl overflow-hidden bg-background-100 border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Cozy%20modern%20workspace%20with%20laptop%20showing%20colorful%20charts%20and%20a%20cup%20of%20coffee%2C%20warm%20natural%20light%2C%20soft%20coral%20accents%2C%20clean%20editorial%20photography%20with%20calm%20composition&width=1100&height=840&seq=about-baogua-01&orientation=landscape"
                alt="爆瓜福利内容整理工作场景"
                title="爆瓜福利 - 内容整理与规则说明"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                <i className="ri-flag-2-line"></i>
                网站定位
              </span>
              <h2 className="mt-4 text-2xl md:text-3xl text-foreground-950 leading-snug">
                一个只为「看懂福利活动」而存在的内容站
              </h2>
              <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed">
                {siteInfo.name} 做的事情很具体：把正在进行的福利活动整理成清单，把每一条规则翻译成直白的话，把奖励到账的时间写在显眼的位置。你不用先去研究活动规则，再来判断值不值得参与——这两步我们帮你合并成一步。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                我们不做夸张的承诺，也不会告诉你「必中大奖」。相反，我们更愿意把概率、库存、次数限制这些容易被忽略的细节摆出来，让你在参与之前心里有底。
              </p>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {stats.map((item) => (
                  <div key={item.label}>
                    <p className="text-xl md:text-2xl text-foreground-950 font-heading">
                      {item.value}
                      <span className="text-sm text-primary-600 ml-0.5">{item.suffix}</span>
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100 border-y border-background-200">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
              <i className="ri-lightbulb-line"></i>
              我们在想什么
            </span>
            <h2 className="mt-4 text-2xl md:text-3xl text-foreground-950">发展理念与自我约束</h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              内容站最怕的不是没人看，而是看了不信任。所以我们对内容本身定了几条硬规矩。
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {positioning.map((item, index) => (
              <Reveal key={item.title} delay={index * 80}>
                <div className="h-full bg-background-50 border border-background-200 rounded-2xl p-6">
                  <span className={`w-12 h-12 rounded-2xl flex items-center justify-center text-2xl ${toneMap[item.tone]}`}>
                    <i className={item.icon}></i>
                  </span>
                  <h3 className="mt-5 text-lg text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {principles.map((item, index) => (
              <Reveal key={item.title} delay={index * 60}>
                <div className="h-full bg-background-50 border border-background-200 rounded-2xl p-5">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-background-100 text-foreground-700 flex items-center justify-center text-lg">
                      <i className={item.icon}></i>
                    </span>
                    <h3 className="text-base text-foreground-950">{item.title}</h3>
                  </div>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
              <i className="ri-chat-heart-line"></i>
              用户口碑
            </span>
            <h2 className="mt-4 text-2xl md:text-3xl text-foreground-950">他们眼中的爆瓜福利</h2>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {testimonials.map((item, index) => (
              <Reveal key={item.name} delay={index * 80}>
                <figure className="h-full bg-background-100 border border-background-200 rounded-2xl p-6 flex flex-col">
                  <span className="text-primary-500 text-lg">
                    <i className="ri-double-quotes-l"></i>
                  </span>
                  <blockquote className="mt-3 text-sm text-foreground-700 leading-relaxed flex-1">{item.text}</blockquote>
                  <figcaption className="mt-5 pt-5 border-t border-background-200 flex items-center gap-3">
                    <span className="w-11 h-11 rounded-full overflow-hidden shrink-0 bg-background-200">
                      <img
                        src={item.avatar}
                        alt={`${item.name} 的头像`}
                        title={`${item.name} - 爆瓜福利用户反馈`}
                        className="w-full h-full object-cover object-top"
                      />
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-950">{item.name}</span>
                      <span className="block text-xs text-foreground-500">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 pb-16 md:pb-24 bg-background-50">
          <div className="bg-background-100 border border-background-200 rounded-2xl p-6 md:p-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div className="max-w-lg">
                <h2 className="text-2xl md:text-3xl text-foreground-950">想和我们聊聊？</h2>
                <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                  无论是对内容有疑问、发现信息有误，还是想推荐一个值得收录的福利活动，都欢迎随时联系我们。
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-mail-send-line"></i>
                    发送邮件
                  </a>
                  <Link
                    to="/faq"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-50 border border-background-200 text-foreground-800 text-sm font-medium whitespace-nowrap hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-question-answer-line"></i>
                    先看常见问题
                  </Link>
                </div>
              </div>

              <div className="w-full lg:max-w-md flex flex-col gap-4">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-start gap-4 bg-background-50 border border-background-200 rounded-2xl p-5">
                    <span className="shrink-0 w-10 h-10 rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center text-xl">
                      <i className={item.icon}></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">{item.label}</p>
                      <p className="mt-1 text-sm text-foreground-900">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
}